import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Eye, EyeOff, Lock, MessagesSquare, MonitorPlay,
  RotateCcw, Sparkles, Zap, TrendingUp, Users,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button, Badge } from '../../components/ui';
import { STAT_CARD_BASE, STAT_CHIP } from '../../components/ui/StatCard';
import KpiStrip from '../../components/simulation/KpiStrip';
import ScenarioBrief from '../../components/simulation/ScenarioBrief';
import DecisionCard from '../../components/simulation/DecisionCard';
import simulationRunService from '../../services/simulationRunService';
import { showError } from '../../utils/toast';
import { deckFor, cardById, movementSense, formatDelta } from '../../utils/simulationEngine';
import { BEHAVIOUR_TAGS } from '../../utils/behaviourTags';

/**
 * Solo rehearsal — one trainer plays every team through a complete run.
 *
 * The SERVER is authoritative: phase, commits and every KPI figure come back from the
 * run API, and this page renders what it is told. It keeps no parallel copy of the
 * score. That is deliberate — the same screens have to keep working when a room full of
 * participants is mutating the same run, and a client that computed its own totals
 * would quietly disagree with the projector the moment anything raced.
 */

const PHASES = [
  { key: 'brief', label: 'Brief' },
  { key: 'negotiate', label: 'Negotiate' },
  { key: 'decide', label: 'Decide' },
  { key: 'reveal', label: 'Reveal' },
  { key: 'debrief', label: 'Debrief' },
];

const SimulationRehearsePage = () => {
  const { simulationId } = useParams();
  const navigate = useNavigate();

  const [simulation, setSimulation] = useState(null);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeFunction, setActiveFunction] = useState(null);
  const [whatIf, setWhatIf] = useState({ functionKey: null, cardId: null });
  const [whatIfResult, setWhatIfResult] = useState(null);
  // Which wildcard the trainer chooses to fire this round. The source material names
  // the tactics but never says which round each belongs to — that is a live call.
  const [eventCardId, setEventCardId] = useState(null);

  const startRun = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simulationRunService.start(simulationId, { mode: 'rehearsal' });
      setSimulation(res.simulation);
      setRun(res.run);
      setActiveFunction(res.simulation.functions[0]?.key || null);
      setWhatIf({ functionKey: null, cardId: null });
      setWhatIfResult(null);
    } catch (err) {
      showError(err.userMessage || err.message || 'Could not start the rehearsal');
      navigate(`/trainer/simulations/${simulationId}`);
    } finally {
      setLoading(false);
    }
  }, [simulationId, navigate]);

  useEffect(() => { startRun(); }, [startRun]);

  const round = useMemo(
    () => simulation?.rounds.find((r) => r.index === run?.roundIndex) || null,
    [simulation, run]
  );

  /** Commits for the current round, as { functionKey: cardId }. */
  const commits = useMemo(() => {
    const m = {};
    (run?.commits || []).forEach((c) => { m[c.functionKey] = c.cardId; });
    return m;
  }, [run]);

  const allCommitted = Boolean(simulation) && (run?.lockedCount || 0) >= (run?.teamCount || 0);

  /** The server's stored result for this round, expanded with card objects for display. */
  const resolved = useMemo(() => {
    if (!simulation || !run) return null;
    const result = run.roundResults.find((r) => r.roundIndex === run.roundIndex);
    if (!result) return null;
    return {
      ...result,
      attribution: result.perFunction.map((p) => {
        const fn = simulation.functions.find((f) => f.key === p.functionKey);
        return { ...p, functionName: fn?.name, variant: fn?.variant, card: cardById(simulation, p.cardId) };
      }),
      event: result.eventCardId ? simulation.eventCards.find((e) => e.id === result.eventCardId) : null,
    };
  }, [simulation, run]);

  /** Cumulative BEFORE this round, so the strip shows a running total plus this delta. */
  const cumulativeBefore = useMemo(() => {
    if (!run) return {};
    const prior = run.roundResults.filter((r) => r.roundIndex < run.roundIndex);
    return prior.length ? prior[prior.length - 1].cumulative : {};
  }, [run]);

  const call = async (fn, failure) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.run) setRun(res.run);
      return res;
    } catch (err) {
      // A 409 means someone (or a double-click) already advanced — adopt the truth.
      if (err.status === 409 && err.data?.run) setRun(err.data.run);
      else showError(err.userMessage || err.message || failure);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const selectCard = (functionKey, cardId) =>
    call(() => simulationRunService.commit(run.id, { functionKey, cardId }), 'Could not record that decision');

  const isLastRound = run && simulation && run.roundIndex >= simulation.rounds.length;

  const advance = async () => {
    const phase = run.phase;
    if (phase === 'brief') {
      return call(() => simulationRunService.advancePhase(run.id, {
        from: 'brief', to: simulation.play?.negotiateEnabled ? 'negotiate' : 'decide',
      }), 'Could not advance');
    }
    if (phase === 'negotiate') {
      return call(() => simulationRunService.advancePhase(run.id, { from: 'negotiate', to: 'decide' }), 'Could not advance');
    }
    if (phase === 'decide') {
      // Score first, then move — so `reveal` never renders without a result behind it.
      const scored = await call(() => simulationRunService.resolve(run.id, eventCardId), 'Could not score that round');
      if (!scored) return null;
      return call(() => simulationRunService.advancePhase(run.id, { from: 'decide', to: 'reveal' }), 'Could not advance');
    }
    if (phase === 'reveal') {
      return call(() => simulationRunService.advancePhase(run.id, { from: 'reveal', to: 'debrief' }), 'Could not advance');
    }
    // debrief
    if (isLastRound) {
      return call(() => simulationRunService.end(run.id), 'Could not close the run');
    }
    setWhatIf({ functionKey: null, cardId: null });
    setWhatIfResult(null);
    setEventCardId(null);
    setActiveFunction(simulation.functions[0].key);
    return call(() => simulationRunService.advancePhase(run.id, {
      from: 'debrief', to: 'brief', roundIndex: run.roundIndex + 1,
    }), 'Could not start the next round');
  };

  const runWhatIf = async (functionKey, cardId) => {
    setWhatIf({ functionKey, cardId });
    if (!functionKey || !cardId) { setWhatIfResult(null); return; }
    try {
      const res = await simulationRunService.whatIf(run.id, { functionKey, altCardId: cardId });
      setWhatIfResult(res.counterfactual);
    } catch (err) {
      showError(err.userMessage || 'Could not compute that comparison');
    }
  };

  if (loading || !simulation || !run) {
    return (
      <DashboardLayout title="Rehearsal">
        <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto">
          <div className={`${STAT_CARD_BASE} animate-pulse p-8`}>
            <div className="h-5 w-1/3 rounded bg-neutral-200 dark:bg-neutral-800" />
            <div className="mt-4 h-3 w-2/3 rounded bg-neutral-100 dark:bg-neutral-800" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  /* ================= closing screen ================= */
  if (run.phase === 'closed') {
    return (
      <DashboardLayout title="Rehearsal complete">
        <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">
          <div className={`${STAT_CARD_BASE} p-6`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Where the numbers ended up</h2>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  {run.roundResults.length} rounds of accumulated decisions. In a live run the room
                  sees the final debrief on the main screen — open it to see what they would.
                </p>
              </div>
              {/* The same room screen a live run uses, for this rehearsal's results. */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(`/trainer/simulation-runs/${run.id}/projector`, 'aktivity-projector', 'noopener')}
              >
                <MonitorPlay size={14} strokeWidth={2} className="mr-1.5" />
                Open the final room screen
              </Button>
            </div>
            <div className="mt-5"><KpiStrip kpis={simulation.kpis} values={run.cumulative} /></div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className={`${STAT_CARD_BASE} p-5`}>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                <TrendingUp size={15} strokeWidth={1.75} className="text-primary-500" />
                Cooperation, round by round
              </h3>
              <div className="mt-4 space-y-2.5">
                {run.cooperationTrend.map((t) => (
                  <div key={t.round} className="flex items-center gap-3">
                    <span className="w-14 shrink-0 text-xs text-neutral-500">Round {t.round}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                      <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${t.cooperation}%` }} />
                    </div>
                    <span className="w-9 shrink-0 text-right text-xs font-semibold tabular-nums text-neutral-600 dark:text-neutral-300">
                      {t.cooperation}%
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                The shape of this line is usually the most useful thing on the screen. A dip at
                round 2 or 3 is the moment the room started protecting its own numbers.
              </p>
            </div>

            <div className={`${STAT_CARD_BASE} p-5`}>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                <Users size={15} strokeWidth={1.75} className="text-primary-500" />
                How the room behaved
              </h3>
              <div className="mt-4 space-y-2">
                {run.archetypeMix.map((m) => {
                  const meta = BEHAVIOUR_TAGS[m.tag];
                  if (!meta) return null;
                  return (
                    <div key={m.tag} className="flex items-center gap-3">
                      <span className={`w-32 shrink-0 rounded-md px-2 py-1 text-[10px] font-semibold ${STAT_CHIP[meta.variant]}`}>
                        {meta.label}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                        <div className="h-full rounded-full bg-neutral-400 dark:bg-neutral-500" style={{ width: `${m.share}%` }} />
                      </div>
                      <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-neutral-600 dark:text-neutral-300">
                        {m.count}×
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="primary-filled" onClick={startRun}>
              <RotateCcw size={15} strokeWidth={2} className="mr-1.5" />
              Run it again
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                try { await simulationRunService.downloadReport(run.id); }
                catch (e) { showError(e.userMessage || 'Could not build the report'); }
              }}
            >
              Debrief PDF
            </Button>
            <Button variant="secondary" onClick={() => navigate('/trainer/simulations/team')}>Back to simulations</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const phaseIdx = PHASES.findIndex((p) => p.key === run.phase);

  return (
    <DashboardLayout title="Rehearsal">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">

        <div className={`${STAT_CARD_BASE} p-4 sm:p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              {/* Back to the simulation this rehearsal belongs to. There is a "Leave
                  rehearsal" link at the very bottom of the page, but a trainer part-way
                  through a round would have to scroll past every deck to reach it, so the
                  way out also belongs where the eye already is. Matches the builder's
                  own back link. */}
              <button
                type="button"
                onClick={() => navigate(`/trainer/simulations/${simulationId}`)}
                className="mb-1.5 inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                <ArrowLeft size={13} strokeWidth={2} />
                Back to simulation
              </button>
              <div className="flex items-center gap-2">
                <Badge variant="primary" size="sm">Round {run.roundIndex} of {simulation.rounds.length}</Badge>
                <Badge variant="default" size="sm">Solo rehearsal</Badge>
              </div>
              <h2 className="mt-2 text-base font-semibold text-neutral-900 dark:text-neutral-100">{round?.title}</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={startRun} disabled={busy}>
              <RotateCcw size={14} strokeWidth={2} className="mr-1.5" />
              Restart
            </Button>
          </div>

          <div className="mt-4 flex items-center gap-1.5 overflow-x-auto pb-1">
            {PHASES.map((p, i) => {
              const done = i < phaseIdx;
              const active = i === phaseIdx;
              return (
                <div key={p.key} className="flex shrink-0 items-center gap-1.5">
                  <span className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    active ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-400'
                    : done ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                    : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500'}`}>
                    {p.label}
                  </span>
                  {i < PHASES.length - 1 && <span className={`h-px w-4 ${done ? 'bg-emerald-300' : 'bg-neutral-200 dark:bg-neutral-700'}`} />}
                </div>
              );
            })}
          </div>
        </div>

        <KpiStrip
          kpis={simulation.kpis}
          values={cumulativeBefore}
          deltas={['reveal', 'debrief'].includes(run.phase) ? resolved?.total : null}
          compact
        />

        {/* No mode="wait" / no exit — a phase whose mount depends on an animation
            callback wedges on a backgrounded tab. See git history for the bug. */}
        <AnimatePresence>
          <motion.div
            key={`${run.roundIndex}-${run.phase}`}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.18 }}
          >
            {run.phase === 'brief' && round && (
              <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                <ScenarioBrief round={round} functions={simulation.functions} />
              </div>
            )}

            {run.phase === 'negotiate' && (
              <div className={`${STAT_CARD_BASE} p-5 sm:p-6`}>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                    <MessagesSquare size={17} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Teams talk to each other</h3>
                    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
                      {Math.round((round?.negotiateSec || 180) / 60)} minutes for the teams to argue it
                      out before anyone commits. Without this phase, &ldquo;Hold a Joint Meeting&rdquo;
                      and &ldquo;Join Root-Cause Analysis&rdquo; are just buttons — collaboration gets
                      claimed instead of practised.
                    </p>
                  </div>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {simulation.functions.map((fn) => (
                    <div key={fn.key} className="rounded-xl border border-neutral-200 p-3.5 dark:border-neutral-700/60">
                      <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAT_CHIP[fn.variant]}`}>{fn.name}</span>
                      <p className="mt-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">{fn.mandate}</p>
                      {fn.privateBrief && (
                        <p className="mt-1.5 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
                          <span className="font-semibold text-amber-600 dark:text-amber-400">Only they know: </span>{fn.privateBrief}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {run.phase === 'decide' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-1.5">
                  {simulation.functions.map((fn) => (
                    <button
                      key={fn.key}
                      type="button"
                      onClick={() => setActiveFunction(fn.key)}
                      className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-all ${
                        activeFunction === fn.key
                          ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                          : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-neutral-300 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                      }`}
                    >
                      {fn.name}
                      {commits[fn.key] && <Lock size={11} strokeWidth={2.5} className="text-emerald-500" />}
                    </button>
                  ))}
                </div>

                {simulation.functions.filter((fn) => fn.key === activeFunction).map((fn) => (
                  <div key={fn.key} className={`${STAT_CARD_BASE} p-5`}>
                    <div className="mb-4">
                      <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAT_CHIP[fn.variant]}`}>{fn.name}</span>
                      {fn.publicBrief && <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{fn.publicBrief}</p>}
                      {fn.privateBrief && <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">{fn.privateBrief}</p>}
                    </div>
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {deckFor(simulation, fn.key).map((card) => (
                        <DecisionCard
                          key={card.id}
                          card={card}
                          selected={commits[fn.key] === card.id}
                          disabled={busy}
                          onSelect={(id) => selectCard(fn.key, id)}
                        />
                      ))}
                    </div>
                    <p className="mt-4 text-[11px] text-neutral-400 dark:text-neutral-500">
                      Participants see only their own team&apos;s cards, and only the title and
                      description — never the numbers or the leadership angle.
                    </p>
                  </div>
                ))}

                {/* The wildcard is the trainer's to play. The source material names the
                    tactics but never says which round each belongs to. */}
                {simulation.eventCards?.length > 0 && (
                  <div className={`${STAT_CARD_BASE} p-5`}>
                    <div className="flex items-start gap-2.5">
                      <Zap size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-500" />
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                          Play a {simulation.meta.eventDeckLabel}?
                        </h3>
                        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                          Optional. Fires when you reveal, and hits every team at once — nobody
                          gets to plan for it.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEventCardId(null)}
                            className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
                              eventCardId === null
                                ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                                : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-neutral-300 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                            }`}
                          >
                            Nothing this round
                          </button>
                          {simulation.eventCards.map((e) => (
                            <button
                              key={e.id}
                              type="button"
                              title={e.effectOnTeams}
                              onClick={() => setEventCardId(e.id)}
                              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
                                eventCardId === e.id
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-white text-neutral-600 ring-1 ring-neutral-200 hover:ring-neutral-300 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700'
                              }`}
                            >
                              {e.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {['reveal', 'debrief'].includes(run.phase) && resolved && (
              <div className="space-y-4">
                {run.phase === 'reveal' && (
                  <div className={`${STAT_CARD_BASE} p-5`}>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">All four, at once</h3>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => call(() => simulationRunService.revealAngles(run.id, !run.anglesRevealed), 'Could not update the reveal')}
                      >
                        {run.anglesRevealed ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
                        {run.anglesRevealed ? 'Hide insight' : 'Reveal insight'}
                      </Button>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      {resolved.attribution.map((a) => (
                        <div key={a.functionKey} className="rounded-xl border border-neutral-200 p-3.5 dark:border-neutral-700/60">
                          <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${STAT_CHIP[a.variant] || STAT_CHIP.slate}`}>
                            {a.functionName}
                          </span>
                          <h4 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">{a.card?.title}</h4>
                          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{a.card?.description}</p>

                          {run.anglesRevealed && a.card?.leadershipAngle && (
                            <div className="mt-3 rounded-lg bg-neutral-50 px-2.5 py-2 dark:bg-neutral-800/60">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">Leadership angle</span>
                              <p className="mt-0.5 text-xs font-medium text-neutral-800 dark:text-neutral-100">{a.card.leadershipAngle}</p>
                            </div>
                          )}

                          <div className="mt-2.5 flex flex-wrap gap-x-2.5 gap-y-1">
                            {simulation.kpis.map((kpi) => {
                              const d = a.kpi?.[kpi.key];
                              if (!d) return null;
                              const sense = movementSense(kpi, d);
                              return (
                                <span key={kpi.key} className="text-[11px]">
                                  <span className="text-neutral-400">{kpi.label} </span>
                                  <span className={`font-semibold tabular-nums ${
                                    sense === 'good' ? 'text-emerald-600 dark:text-emerald-400'
                                    : sense === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-neutral-400'}`}>
                                    {formatDelta(d)}
                                  </span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {run.phase === 'reveal' && resolved.event && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
                    <div className="flex items-start gap-2.5">
                      <Zap size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                      <div>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                          {simulation.meta.eventDeckLabel}
                        </span>
                        <h4 className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100">{resolved.event.title}</h4>
                        <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-300">
                          {resolved.event.description} — {resolved.event.effectOnTeams}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {run.phase === 'debrief' && round && (
                  <>
                    {round.debrief?.teachingPoint && (
                      <div className={`${STAT_CARD_BASE} border-l-4 border-l-primary-500 p-5`}>
                        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                          <Sparkles size={11} strokeWidth={2.5} />Teaching point
                        </span>
                        <p className="mt-1.5 text-sm leading-relaxed text-neutral-800 dark:text-neutral-100">{round.debrief.teachingPoint}</p>
                      </div>
                    )}

                    {round.debrief?.facilitatorQuestions?.length > 0 && (
                      <div className={`${STAT_CARD_BASE} p-5`}>
                        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Ask the room</h3>
                        <ul className="mt-3 space-y-2">
                          {round.debrief.facilitatorQuestions.map((q) => (
                            <li key={q.text} className="flex items-start gap-2.5">
                              <span className="mt-0.5 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-neutral-400 ring-1 ring-neutral-200 dark:ring-neutral-700">{q.phase}</span>
                              <span className="text-sm text-neutral-700 dark:text-neutral-200">{q.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className={`${STAT_CARD_BASE} p-5`}>
                      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                        &ldquo;What if we&apos;d done it differently?&rdquo;
                      </h3>
                      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        Swap one team&apos;s choice and watch the numbers move. Use it to open a
                        question, not to announce a right answer.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <select
                          value={whatIf.functionKey || ''}
                          onChange={(e) => runWhatIf(e.target.value || null, null)}
                          className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
                        >
                          <option value="">Pick a team…</option>
                          {simulation.functions.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
                        </select>
                        {whatIf.functionKey && (
                          <select
                            value={whatIf.cardId || ''}
                            onChange={(e) => runWhatIf(whatIf.functionKey, e.target.value || null)}
                            className="min-w-0 flex-1 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
                          >
                            <option value="">…had chosen instead</option>
                            {deckFor(simulation, whatIf.functionKey)
                              .filter((c) => c.id !== commits[whatIf.functionKey])
                              .map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                          </select>
                        )}
                      </div>

                      {whatIfResult && (
                        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 rounded-xl bg-neutral-50 px-4 py-3 dark:bg-neutral-800/60">
                          {simulation.kpis.map((kpi) => {
                            const d = whatIfResult.delta[kpi.key];
                            const sense = movementSense(kpi, d);
                            return (
                              <div key={kpi.key}>
                                <p className="text-[10px] uppercase tracking-wide text-neutral-400">{kpi.label}</p>
                                <p className={`text-sm font-semibold tabular-nums ${
                                  sense === 'good' ? 'text-emerald-600 dark:text-emerald-400'
                                  : sense === 'bad' ? 'text-red-600 dark:text-red-400' : 'text-neutral-400'}`}>
                                  {formatDelta(d)}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-neutral-400 dark:text-neutral-500">
            {run.phase === 'decide' && !allCommitted
              ? `${run.lockedCount} of ${run.teamCount} teams have chosen`
              : 'You control the pace — nothing advances on its own.'}
          </p>
          <Button
            variant="primary-filled"
            onClick={advance}
            disabled={busy || (run.phase === 'decide' && !allCommitted)}
            loading={busy}
          >
            {run.phase === 'brief' && (simulation.play?.negotiateEnabled ? 'Open negotiation' : 'Lock in decisions')}
            {run.phase === 'negotiate' && 'Lock in decisions'}
            {run.phase === 'decide' && 'Reveal all teams'}
            {run.phase === 'reveal' && 'Go to debrief'}
            {run.phase === 'debrief' && (isLastRound ? 'Finish and show the trend' : `Start round ${run.roundIndex + 1}`)}
            <ArrowRight size={15} strokeWidth={2} className="ml-1.5" />
          </Button>
        </div>

        <button
          type="button"
          onClick={() => navigate('/trainer/simulations/team')}
          className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
        >
          <ArrowLeft size={13} strokeWidth={2} />Leave rehearsal
        </button>
      </div>
    </DashboardLayout>
  );
};

export default SimulationRehearsePage;
